import { getCookie } from "./utils";
import axiosInstance from "../services/api/axiosInstance";

type ErrorData = {
  message: string;
  stack?: string;
  type:
    | "window.onerror"
    | "unhandledrejection"
    | "axios.error"
    | "axiosFast.error"
    | "console.error";
  timestamp: string;
  requestUrl?: string;
  method?: string;
  status?: number;
  source?: string;
  lineno?: number;
  colno?: number;
};

const errorCache = new Map<string, number>();
const DEDUP_TTL = 60_000;

function getErrorKey(error: ErrorData): string {
  const normalizedMessage = error.message?.replace(/\s+/g, " ").trim();
  return `${error.requestUrl || ""}::${error.status || ""}::${normalizedMessage}`;
}
export function sendErrorToServer(errorData: ErrorData) {
  if (
    errorData.message === "canceled" ||
    errorData.message?.includes("canceled")
  ) {
    return;
  }
  const now = Date.now();
  const key = getErrorKey(errorData);
  const lastSent = errorCache.get(key);

  if (lastSent && now - lastSent < DEDUP_TTL) {
    return;
  }
  errorCache.set(key, now);
  // Clean up memory if too large
  if (errorCache.size > 100) {
    const sorted = [...errorCache.entries()].sort((a, b) => a[1] - b[1]);
    sorted.slice(0, 50).forEach(([k]) => errorCache.delete(k));
  }
  const csrfToken = getCookie("csrftoken") || "";
  try {
    const encodedMessage = btoa(encodeURIComponent(JSON.stringify(errorData)));
    axiosInstance
      .post(
        "/jsErrorLog/",
        new URLSearchParams({ msg: encodedMessage }).toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRFToken": csrfToken,
          },
        },
      )
      .catch(() => {});
  } catch (err) {
    console.warn("Failed to encode/send error:", err);
  }
}

function handleWindowError(
  message: string | Event,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error,
) {
  try {
    const stack = error?.stack;
    const errorData: ErrorData = {
      message: String(message),
      stack,
      type: "window.onerror",
      timestamp: new Date().toISOString(),
      ...(source && { source }),
      ...(typeof lineno === "number" && { lineno }),
      ...(typeof colno === "number" && { colno }),
    };
    sendErrorToServer(errorData);
  } catch (e) {
    console.warn("Error inside handleWindowError:", e);
  }
  return true;
}

function handleRejection(event: PromiseRejectionEvent) {
  try {
    const errorData: ErrorData = {
      message: event?.reason?.message || String(event.reason),
      stack: event?.reason?.stack,
      type: "unhandledrejection",
      timestamp: new Date().toISOString(),
    };
    sendErrorToServer(errorData);
  } catch (e) {
    console.warn("Error inside unhandledrejection handler:", e);
  }
}

let originalConsoleError: typeof console.error;

export function initializeErrorHandling() {
  window.onerror = handleWindowError;
  window.addEventListener("unhandledrejection", handleRejection);

  originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    try {
      let message = "";
      let stack: string | undefined = undefined;
      let method: string | undefined = undefined;
      let requestUrl: string | undefined = undefined;
      let status: number | undefined = undefined;

      for (const arg of args) {
        if (arg instanceof Error) {
          message += arg.message + " ";
          stack = arg.stack || stack;

          // If it's an AxiosError with a config
          if ((arg as any).config && typeof (arg as any).config === "object") {
            const axiosErr = arg as any;
            method = axiosErr.config.method?.toUpperCase?.();
            requestUrl = axiosErr.config.url;
            status = axiosErr.response?.status;
          }
        } else if (typeof arg === "object" && arg !== null) {
          try {
            // Try to extract from object shape
            if ("message" in arg && typeof arg.message === "string") {
              message += arg.message + " ";
            }
            if ("stack" in arg && typeof arg.stack === "string") {
              stack = arg.stack;
            }
            if ("config" in arg) {
              method = arg.config?.method?.toUpperCase?.();
              requestUrl = arg.config?.url;
            }
            if ("response" in arg && typeof arg.response?.status === "number") {
              status = arg.response.status;
            }

            message += JSON.stringify(arg) + " ";
          } catch {
            message += "[Unserializable Object] ";
          }
        } else {
          message += String(arg) + " ";
        }
      }

      const errorData: ErrorData = {
        message: message.trim(),
        stack,
        type: "console.error",
        timestamp: new Date().toISOString(),
        ...(method && { method }),
        ...(requestUrl && { requestUrl }),
        ...(status !== undefined && { status }),
      };

      sendErrorToServer(errorData);
    } catch (e) {
      originalConsoleError("Failed to log console.error:", e);
    }
    originalConsoleError(...args);
  };
}

export function removeErrorHandling() {
  window.onerror = null;
  window.removeEventListener("unhandledrejection", handleRejection);
  if (originalConsoleError) {
    console.error = originalConsoleError;
  }
}
